import { useState, useRef, useCallback, useEffect } from 'react';
import { createAudioContext, ensureAudioContextRunning, stopAllAudio } from '../utilities/audioUtils';
import { STOP_PLAYBACK_EVENT } from '../utilities/playbackControl';

/**
 * Custom hook for managing Google Gemini Live API conversations
 * Mirrors the ElevenLabs useConversation hook API for compatibility
 */
export const useGeminiConversation = (config = {}) => {
  const {
    micMuted = false,
    onConnect = () => {},
    onDisconnect = () => {},
    onMessage = () => {},
    onError = () => {},
    onDebug = () => {},
    onModeChange = () => {},
    onStatusChange = () => {},
    onUnhandledClientToolCall = () => {},
  } = config;

  // State
  const [status, setStatus] = useState('disconnected');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [mode, setMode] = useState('listening');

  // Refs for WebSocket and audio management
  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioWorkletNodeRef = useRef(null);
  const conversationIdRef = useRef(null);
  const audioQueueRef = useRef(new Set()); // Set of active AudioBufferSourceNode (for interruption)
  const nextStartTimeRef = useRef(0); // Time-based scheduling for gapless playback
  const isConnectingRef = useRef(false); // Prevent duplicate connections
  const currentTranscriptionRef = useRef({ user: '', model: '' }); // Accumulate transcriptions

  // Internal state tracking
  const isMutedRef = useRef(micMuted);

  // Store latest callbacks in refs to avoid stale closures
  const onMessageRef = useRef(onMessage);
  const onErrorRef = useRef(onError);
  const onDebugRef = useRef(onDebug);
  const onModeChangeRef = useRef(onModeChange);
  const onUnhandledClientToolCallRef = useRef(onUnhandledClientToolCall);

  // Update refs when callbacks change
  useEffect(() => {
    onMessageRef.current = onMessage;
    onErrorRef.current = onError;
    onDebugRef.current = onDebug;
    onModeChangeRef.current = onModeChange;
    onUnhandledClientToolCallRef.current = onUnhandledClientToolCall;
  }, [onMessage, onError, onDebug, onModeChange, onUnhandledClientToolCall]);

  // Update muted state when prop changes
  useEffect(() => {
    isMutedRef.current = micMuted;
    onDebugRef.current({ type: 'mute_state_change', muted: micMuted });
  }, [micMuted]);

  const stopPlayback = useCallback((reason = 'manual') => {
    stopAllAudio({ nextStartTimeRef, audioQueueRef });
    setIsSpeaking(false);
    setMode('listening');
    onModeChangeRef.current({ mode: 'listening' });
    onDebugRef.current({ type: 'playback_stopped', reason });
  }, []);

  useEffect(() => {
    const handleStopPlayback = (event) => {
      stopPlayback(event?.detail?.reason || 'global-event');
    };

    window.addEventListener(STOP_PLAYBACK_EVENT, handleStopPlayback);
    return () => window.removeEventListener(STOP_PLAYBACK_EVENT, handleStopPlayback);
  }, [stopPlayback]);

  /**
   * Initialize Audio Context and microphone stream
   */
  const initializeAudio = useCallback(async () => {
    try {
      // Create audio context
      audioContextRef.current = createAudioContext(16000);

      // Same silent failure as the ElevenLabs path: a suspended context captures nothing
      // and reports nothing. Start it before anything is hung off it.
      await ensureAudioContextRunning(audioContextRef.current);

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
          sampleRate: 16000,
        },
      });

      mediaStreamRef.current = stream;

      // Create audio source from microphone
      const source = audioContextRef.current.createMediaStreamSource(stream);

      // Create script processor for audio chunks (legacy but widely supported)
      // In production, consider using AudioWorklet for better performance
      const processor = audioContextRef.current.createScriptProcessor(4096, 1, 1);

      processor.onaudioprocess = (e) => {
        if (!isMutedRef.current && wsRef.current?.readyState === WebSocket.OPEN) {
          const inputData = e.inputBuffer.getChannelData(0);

          // Convert Float32Array to Int16Array (PCM16)
          const pcm16 = new Int16Array(inputData.length);
          for (let i = 0; i < inputData.length; i++) {
            const s = Math.max(-1, Math.min(1, inputData[i]));
            pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
          }

          // Send audio data to Gemini
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            const audioMessage = {
              realtimeInput: {
                mediaChunks: [
                  {
                    data: btoa(String.fromCharCode.apply(null, new Uint8Array(pcm16.buffer))),
                    mimeType: 'audio/pcm',
                  },
                ],
              },
            };
            wsRef.current.send(JSON.stringify(audioMessage));
          }
        }
      };

      source.connect(processor);
      processor.connect(audioContextRef.current.destination);
      audioWorkletNodeRef.current = processor;

      return { audioContext: audioContextRef.current, stream };
    } catch (error) {
      console.error('Error initializing audio:', error);
      onErrorRef.current(error.message, { type: 'audio_initialization_error' });
      throw error;
    }
  }, []);

  /**
   * Play next audio buffer from queue
   */
  // Removed old sequential playback - now using time-based scheduling like Google sample

  /**
   * Play audio response from Gemini (base64 encoded)
   * Uses time-based scheduling for gapless playback (Google sample approach)
   */
  const playAudioChunk = useCallback(async (audioData) => {
    // Ensure single AudioContext at 24kHz for Gemini output
    if (!audioContextRef.current) {
      audioContextRef.current = createAudioContext(24000);
      console.log('✅ Created AudioContext at 24kHz for Gemini output');
      nextStartTimeRef.current = 0;
    }

    try {
      const outputCtx = audioContextRef.current;

      // A suspended context makes the agent appear mute rather than broken.
      await ensureAudioContextRunning(outputCtx);

      // Decode base64 to raw PCM16 little-endian data
      const binaryString = atob(audioData);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Convert Int16 PCM to Float32 for Web Audio API
      const int16Array = new Int16Array(bytes.buffer);
      const float32Array = new Float32Array(int16Array.length);

      // Normalize Int16 to Float32 (Google sample approach)
      for (let i = 0; i < int16Array.length; i++) {
        float32Array[i] = int16Array[i] / 32768.0;
      }

      // Create audio buffer at 24kHz
      const frameCount = int16Array.length;
      const audioBuffer = outputCtx.createBuffer(1, frameCount, 24000);
      audioBuffer.getChannelData(0).set(float32Array);

      // Time-based scheduling for gapless playback (Google sample approach)
      nextStartTimeRef.current = Math.max(nextStartTimeRef.current, outputCtx.currentTime);

      const source = outputCtx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(outputCtx.destination);

      // Track source for interruption handling
      source.addEventListener('ended', () => {
        audioQueueRef.current.delete(source);
        // Update speaking state when queue is empty
        if (audioQueueRef.current.size === 0) {
          setIsSpeaking(false);
          setMode('listening');
          onModeChangeRef.current({ mode: 'listening' });
        }
      });

      // Schedule audio to start at calculated time (gapless)
      source.start(nextStartTimeRef.current);
      nextStartTimeRef.current += audioBuffer.duration;

      // Add to active queue
      audioQueueRef.current.add(source);

      // Update speaking state
      if (!isSpeaking) {
        setIsSpeaking(true);
        setMode('speaking');
        onModeChangeRef.current({ mode: 'speaking' });
      }

      console.log(`🔊 Scheduled audio: start=${nextStartTimeRef.current - audioBuffer.duration}, duration=${audioBuffer.duration}s, queue=${audioQueueRef.current.size}`);

    } catch (error) {
      console.error('Error playing audio chunk:', error);
      onErrorRef.current(error.message, { type: 'audio_playback_error' });
    }
  }, [isSpeaking]);

  /**
   * Handle JSON message from Gemini (extracted for reuse)
   */
  const handleJSONMessage = useCallback((data) => {
    onDebugRef.current({ type: 'websocket_message', data });

    // Handle errors from Gemini
    if (data.error) {
      console.error('❌ Gemini API Error:', data.error);
      onErrorRef.current(`Gemini API Error: ${data.error.message || JSON.stringify(data.error)}`, {
        type: 'gemini_api_error',
        error: data.error
      });
      return;
    }

    // Handle different message types from Gemini Live API
    if (data.serverContent) {
      const { modelTurn, turnComplete, outputTranscription, inputTranscription, interrupted } = data.serverContent;

      // DEBUG: Log full serverContent structure to see what fields are present
      console.log('🔍 DEBUG serverContent:', {
        hasOutputTranscription: !!outputTranscription,
        hasInputTranscription: !!inputTranscription,
        outputText: outputTranscription?.text || 'null',
        inputText: inputTranscription?.text || 'null',
        hasTurnComplete: !!turnComplete,
        hasInterrupted: !!interrupted,
        keys: Object.keys(data.serverContent)
      });

      // Handle interruption - stop all playing audio (Google sample approach)
      if (interrupted) {
        console.log('⚡ Conversation interrupted - stopping all audio');
        stopPlayback('interruption');
      }

      // Accumulate transcriptions (text comes from separate transcription fields, not parts)
      if (outputTranscription?.text) {
        console.log('📝 Model transcription:', outputTranscription.text);
        currentTranscriptionRef.current.model += outputTranscription.text;
      }
      if (inputTranscription?.text) {
        console.log('🎤 User transcription:', inputTranscription.text);
        currentTranscriptionRef.current.user += inputTranscription.text;
      }

      // Handle model response parts (for audio only now)
      if (modelTurn && modelTurn.parts) {
        modelTurn.parts.forEach((part, index) => {
          console.log(`📝 Processing part ${index}:`, {
            hasText: !!part.text,
            hasAudio: !!part.inlineData?.data,
          });

          // Audio response (base64 encoded in JSON)
          if (part.inlineData?.data) {
            console.log('🔊 Processing base64 audio from inlineData');
            playAudioChunk(part.inlineData.data);
          }
        });
      }

      // Send transcriptions to chat
      if (turnComplete) {
        console.log('✅ Turn complete - sending final transcriptions:', currentTranscriptionRef.current);

        // Send final model text
        if (currentTranscriptionRef.current.model) {
          onMessageRef.current({
            message: currentTranscriptionRef.current.model,
            source: 'ai',
            isFinal: true
          });
        }

        // Send final user text
        if (currentTranscriptionRef.current.user) {
          onMessageRef.current({
            message: currentTranscriptionRef.current.user,
            source: 'user',
            isFinal: true
          });
        }

        // Reset transcriptions for next turn
        currentTranscriptionRef.current = { user: '', model: '' };
        onDebugRef.current({ type: 'turn_complete' });
      } else if (outputTranscription || inputTranscription) {
        // Send intermediate transcriptions (not final)
        console.log('📝 Intermediate transcriptions:', currentTranscriptionRef.current);

        if (currentTranscriptionRef.current.model) {
          onMessageRef.current({
            message: currentTranscriptionRef.current.model,
            source: 'ai',
            isFinal: false
          });
        }

        if (currentTranscriptionRef.current.user) {
          onMessageRef.current({
            message: currentTranscriptionRef.current.user,
            source: 'user',
            isFinal: false
          });
        }
      }
    }

    // Note: User transcriptions now handled via inputTranscription above
    // (userContent handling removed to avoid duplicates)

    // Handle setup completion
    if (data.setupComplete) {
      onDebugRef.current({ type: 'setup_complete' });
    }

    // Handle tool calls (if any)
    if (data.toolCall) {
      onDebugRef.current({ type: 'tool_call', toolCall: data.toolCall });
      onUnhandledClientToolCallRef.current(data.toolCall);
    }
  }, [playAudioChunk, stopPlayback]);

  /**
   * Handle incoming WebSocket messages
   */
  const handleWebSocketMessage = useCallback((event) => {
    try {
      // Handle Blob data (try to parse as JSON first, as Gemini sends JSON messages)
      if (event.data instanceof Blob) {
        onDebugRef.current({ type: 'blob_received', size: event.data.size });

        // Skip tiny blobs (likely keep-alive packets)
        if (event.data.size < 200) {
          console.log(`⏭️ Skipping tiny blob (${event.data.size} bytes) - likely keep-alive`);
          return;
        }

        console.log(`📦 Received blob (${event.data.size} bytes) - attempting to parse as JSON first`);

        // Try to parse as text/JSON first (Gemini Live API sends JSON, not raw PCM)
        const reader = new FileReader();
        reader.onload = async () => {
          try {
            const text = reader.result;
            // Try parsing as JSON
            try {
              const data = JSON.parse(text);
              console.log('✅ Blob was JSON - processing as serverContent');
              // Process as JSON message (falls through to JSON handling below)
              handleJSONMessage(data);
              return;
            } catch (jsonError) {
              // Not JSON - this shouldn't happen with Gemini Live API
              console.error('❌ Blob is not JSON - unexpected format from Gemini', { text: text.substring(0, 100) });
              onErrorRef.current('Received non-JSON blob from Gemini', { type: 'unexpected_blob_format' });
            }
          } catch (error) {
            console.error('Error processing Blob:', error);
          }
        };
        reader.readAsText(event.data); // Read as text to check if it's JSON
        return;
      }

      // Handle string data
      const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      handleJSONMessage(data);
    } catch (error) {
      console.error('Error handling WebSocket message:', error);
      onErrorRef.current(error.message, { type: 'message_parsing_error' });
    }
  }, [handleJSONMessage]);

  /**
   * Start conversation session
   */
  const startSession = useCallback(async (sessionConfig = {}) => {
    try {
      stopPlayback('session-start');

      // Prevent duplicate connections (React Strict Mode protection)
      if (isConnectingRef.current) {
        console.warn('⚠️ Connection already in progress, ignoring duplicate startSession call');
        return null;
      }

      if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
        console.warn('⚠️ WebSocket already exists, closing old connection first');
        wsRef.current.close();
        wsRef.current = null;
      }

      isConnectingRef.current = true; // Set connecting flag

      setStatus('connecting');
      onStatusChange({ status: 'connecting' });

      // Session config is now passed to the proxy instead of used directly
      const sessionConfigForProxy = sessionConfig;

      // Generate conversation ID first
      const conversationId = `gemini-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      conversationIdRef.current = conversationId;

      // Initialize audio after generating ID (so we can still connect if audio fails)
      try {
        await initializeAudio();
        console.log('✅ Audio initialized successfully');
      } catch (audioError) {
        console.warn('⚠️ Audio initialization failed, continuing without microphone:', audioError);
        // Continue anyway - we can still receive messages
      }

      // Connect to Cloud Run WebSocket Proxy instead of direct Vertex AI
      // Proxy handles prompt generation and Vertex AI connection server-side
      const { _config } = await import('../Constants');
      const proxyBaseUrl = _config.wsProxyUrl;
      const wsUrl = `${proxyBaseUrl.replace('https://', 'wss://')}/ws/gemini`;

      console.log(`🌐 Connecting to Cloud Run WebSocket Proxy: ${wsUrl}`);
      console.log(`🔒 Prompts will be generated server-side (secure)`);

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      // Attach event handlers immediately to avoid race conditions
      ws.onopen = () => {
        console.log('🟢 WebSocket Proxy connected, readyState:', ws.readyState);

        // Send init message to proxy with session configuration
        // Proxy will generate prompt server-side and establish Vertex AI connection
        const initMessage = {
          type: 'init',
          config: sessionConfigForProxy
        };

        console.log('📤 Sending init message to proxy:', {
          hasProjectId: !!sessionConfigForProxy.projectId,
          hasModel: !!sessionConfigForProxy.model,
          voice: sessionConfigForProxy.voice || 'Zephyr',
          messageSize: JSON.stringify(initMessage).length,
          readyState: ws.readyState
        });

        try {
          ws.send(JSON.stringify(initMessage));
          console.log('✅ Init message sent successfully');

          // Longer delay to ensure message is fully transmitted before state updates
          // This prevents React cleanup from closing connection before message arrives at server
          setTimeout(() => {
            if (ws.readyState === WebSocket.OPEN) {
              setStatus('connected');
              onStatusChange({ status: 'connected' });
              onConnect({ conversationId });
              isConnectingRef.current = false; // Clear connecting flag
              console.log('✅ Connection established, waiting for server response...');
            } else {
              console.error('❌ WebSocket closed before status update, state:', ws.readyState);
              isConnectingRef.current = false; // Clear connecting flag on failure
            }
          }, 200);

        } catch (sendError) {
          console.error('❌ Failed to send init message:', sendError);
          onErrorRef.current('Failed to send init message', { type: 'send_error', error: sendError });
          return;
        }
      };

      ws.onmessage = handleWebSocketMessage;

      ws.onerror = (error) => {
        console.error('🔴 Vertex AI WebSocket error:', error);
        isConnectingRef.current = false; // Clear connecting flag on error
        onErrorRef.current('Vertex AI WebSocket connection error', { type: 'websocket_error', error });
      };

      ws.onclose = (event) => {
        console.log('🟡 Vertex AI WebSocket closed', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean
        });
        isConnectingRef.current = false; // Clear connecting flag on close
        setStatus('disconnected');
        onStatusChange({ status: 'disconnected' });
        onDisconnect();

        // Cleanup
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach(track => track.stop());
        }
        if (audioWorkletNodeRef.current) {
          audioWorkletNodeRef.current.disconnect();
        }
        if (audioContextRef.current) {
          audioContextRef.current.close();
        }
      };

      return conversationId;
    } catch (error) {
      console.error('Failed to start Vertex AI Gemini conversation:', error);
      isConnectingRef.current = false; // Clear connecting flag on error
      setStatus('disconnected');
      onStatusChange({ status: 'disconnected' });
      onErrorRef.current(error.message, { type: 'session_start_error' });
      throw error;
    }
  }, [initializeAudio, handleWebSocketMessage, onConnect, onDisconnect, onStatusChange, stopPlayback]);

  /**
   * End conversation session
   */
  const endSession = useCallback(async () => {
    setStatus('disconnecting');
    onStatusChange({ status: 'disconnecting' });

    stopPlayback('session-end');

    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    // Stop media stream
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }

    // Disconnect audio processor
    if (audioWorkletNodeRef.current) {
      audioWorkletNodeRef.current.disconnect();
      audioWorkletNodeRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current) {
      await audioContextRef.current.close();
      audioContextRef.current = null;
    }

    setStatus('disconnected');
    onStatusChange({ status: 'disconnected' });
    onDisconnect();
  }, [onDisconnect, onStatusChange, stopPlayback]);

  return {
    status,
    isSpeaking,
    mode,
    startSession,
    endSession,
    stopPlayback,
  };
};
